import { Anchor, Card, Group, Popover, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import {
    humanizeDocType,
    type DocumentSortKey,
    type DocumentSortState,
    type DocumentUsage,
    type MediaGroup
} from '../engine';
import { useCatalogLabels } from '../labels';
import { DocumentLanguagePicker } from './DocumentLanguagePicker';
import { OVERLAY_DROPDOWN_Z_INDEX } from './headerLayout';
import { ProductImage } from './ProductImage';
import { WishlistButton } from './WishlistButton';

import type { DocumentIndexItem } from '../types';

interface DocumentSearchResultsProps {
    /** Already filtered/scored/grouped/paginated by the caller (see engine.selectDocuments) -
     * this component only renders the current page, one row per logical document (language
     * variants collapsed - see MediaGroup). */
    items: MediaGroup<DocumentIndexItem>[];
    /** assetId -> the full set of articles/nodes referencing it (see engine.buildDocumentUsageIndex) -
     * feeds the "Verwendung" column, independent of which single row got deduplicated into view.
     * OMITTED where that column makes no sense: on an article's own Dokumente tab every row is used
     * by exactly this one article (see chat: "Nur die Spalte Verwendung brauchen wir nicht"), and
     * the column then isn't rendered at all. */
    usageIndex?: Map<string, DocumentUsage>;
    locale: string;
    /** Current column sort - omitted (e.g. a caller that doesn't offer sorting) hides the affordance
     * and leaves the list in whatever order it was handed over. */
    sort?: DocumentSortState;
    onSort?: (key: DocumentSortKey) => void;
    /** Set when this list is a system's Dokumente tab: the usage counts are then scoped to that
     * system (the caller builds `usageIndex` from its own document subset), which is what the
     * column header says - and the overlay gets the same context through the link. */
    systemNodeId?: string;
    /** Prefix for the (relative) link into the document overlay. Empty from a list that sits
     * directly under /catalog or /catalog/wishlist; `'../'` from inside the article overlay, whose
     * own route is one level deeper. */
    documentLinkBase?: string;
    /** Selects a tree node in the document-mode navigation (Verwendung popup's "Systeme" entries) -
     * only needed where the Verwendung column is shown. */
    onSelectNode?: (nodeId: string) => void;
}

/** Clickable column head with the same arrow affordance the product list uses (see ProductGrid's
 * ColumnHeader) - a second click on the same column flips the direction. */
const SortableTh = ({
    label,
    sortKey,
    sort,
    onSort,
    width
}: {
    label: string;
    sortKey: DocumentSortKey;
    sort?: DocumentSortState;
    onSort?: (key: DocumentSortKey) => void;
    width?: number;
}) => {
    const isSorted = sort?.key === sortKey;

    return (
        <Table.Th w={width}>
            {onSort ? (
                <UnstyledButton className="flex items-center gap-1" onClick={() => onSort(sortKey)}>
                    <Text fw={600} size="sm">
                        {label}
                    </Text>
                    {isSorted && (
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden>
                            {sort?.dir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                        </span>
                    )}
                </UnstyledButton>
            ) : (
                label
            )}
        </Table.Th>
    );
};

// Relative, same reasoning as ProductGrid's openHref: this table renders under /catalog (document
// search, a system's Dokumente tab) and under /catalog/wishlist, and the article overlay should
// open over whichever of the two the user is looking at.
const articleHref = (artNr: string) => `article/${encodeURIComponent(artNr)}`;
// One path for both cases, relative like articleHref above: the PDF opens as an overlay over this
// very list (see components/DetailOverlay.tsx), and the viewer resolves everything it needs from
// the document index itself - it no longer needs an article to hang off.
//
// `?system=` carries the context this list was rendered in (a system's Dokumente tab) into the
// overlay, which uses it for its own "Verwendung System" tab. In the URL rather than in router
// state on purpose: it survives a reload and a shared link, and its absence is exactly what "opened
// from the document search" means.
const documentHref = (assetId: string, systemNodeId?: string, base = '') =>
    `${base}document/${encodeURIComponent(assetId)}${systemNodeId ? `?system=${encodeURIComponent(systemNodeId)}` : ''}`;

const emptyUsage: DocumentUsage = { articles: [], nodes: [] };

// Edge length of the document preview image (see chat: "Bildspalte würde ich hinter die Merkliste
// setzen, dummy mit 40x40 px").
const THUMBNAIL_SIZE = 40;

/**
 * Preview image of a document - for now deliberately a placeholder: the document index carries no
 * preview asset yet ("Bilder kommen bis März", see chat). ProductImage already renders
 * public/sample-docs/placeholder.svg when it has no source, so the column looks right today and
 * lights up on its own the moment a real URL is passed in here - no request is fired in the
 * meantime, unlike guessing at an assets/tn/<assetId> URL that would 404 on every row until then.
 */
const DocumentThumbnail = ({ alt }: { alt: string }) => (
    <ProductImage alt={alt} height={THUMBNAIL_SIZE} width={THUMBNAIL_SIZE} />
);

/**
 * Resolves which language variant of a document group is currently selected/displayed, and the
 * click-through target that follows from it - shared by DocumentRow (table, `md` and up) and
 * DocumentCard (stacked mobile card) so the two layouts can't drift apart.
 */
const useResolvedDocument = (
    group: MediaGroup<DocumentIndexItem>,
    locale: string,
    usageIndex?: Map<string, DocumentUsage>,
    systemNodeId?: string,
    documentLinkBase?: string
) => {
    const navigate = useNavigate();
    // Defaults to the current UI locale's variant if the document has one, else the group's
    // stable representative - matches the legacy DOPI application's per-row language switcher.
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
        group.variants.find((v) => v.language === locale)?.language ?? group.primary.language ?? null
    );
    // Both sides normalized through the same '' sentinel the Select's own options use for a
    // variant with no language (value={''}/label='–') - comparing the raw fields directly would
    // never match a `null`/`undefined` v.language against the selected '' value.
    const doc = group.variants.find((v) => (v.language ?? '') === (selectedLanguage ?? '')) ?? group.primary;

    const usage = usageIndex?.get(doc.assetId) ?? emptyUsage;
    // The row's own first article target (if any); otherwise fall back to the usage index, which
    // covers the variant-resolved document too - only a document with ZERO article links has no
    // primaryArtNr at all.
    const primaryArtNr = doc.artNrs?.[0] ?? usage.articles[0]?.id;
    const isPdf = doc.mimeType === 'application/pdf';
    const open = () => {
        if (!isPdf) {
            if (primaryArtNr) {
                navigate(articleHref(primaryArtNr));
            }
            return;
        }
        // The state is a fallback only (a document the index doesn't carry) - the viewer resolves
        // url/metadata from the index by assetId, so a reload or a shared link works too.
        navigate(documentHref(doc.assetId, systemNodeId, documentLinkBase), {
            state: { url: doc.url, title: doc.descr }
        });
    };

    return { doc, selectedLanguage, setSelectedLanguage, usage, open };
};

/** The "N Artikel, M Systeme" trigger + its dropdown listing every usage site - identical in the
 * table row and the mobile card, just laid out differently around it. A count of zero is left out
 * of the label entirely (see chat: "gut, aber 0 ... nicht auflisten") - "1 System" rather than
 * "0 Artikel, 1 Systeme"; a document with no usage at all shows a plain dash and no popover, since
 * there would be nothing to open. */
const UsageTrigger = ({ usage, onSelectNode }: { usage: DocumentUsage; onSelectNode: (nodeId: string) => void }) => {
    const { L } = useCatalogLabels();
    const navigate = useNavigate();

    const label = [
        usage.articles.length > 0 ? `${usage.articles.length} ${L.documentUsageArticles}` : undefined,
        usage.nodes.length > 0 ? `${usage.nodes.length} ${L.documentUsageNodes}` : undefined
    ]
        .filter(Boolean)
        .join(', ');

    if (!label) {
        return (
            <Text c="dimmed" size="sm">
                –
            </Text>
        );
    }

    // zIndex: this table also renders inside the detail overlay, so its portaled dropdown needs to
    // sit above it - see OVERLAY_DROPDOWN_Z_INDEX.
    return (
        <Popover position="bottom-end" shadow="md" zIndex={OVERLAY_DROPDOWN_Z_INDEX} withArrow>
            <Popover.Target>
                <UnstyledButton
                    className="text-sm underline decoration-dotted"
                    onClick={(event) => event.stopPropagation()}
                >
                    {label}
                </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={4}>
                    {usage.articles.length > 0 && (
                        <>
                            <Text fw={600} size="xs">
                                {L.documentUsageArticles}
                            </Text>
                            {usage.articles.map((a) => (
                                <Anchor key={a.id} size="sm" onClick={() => navigate(articleHref(a.id))}>
                                    {a.name}
                                </Anchor>
                            ))}
                        </>
                    )}
                    {usage.nodes.length > 0 && (
                        <>
                            <Text fw={600} mt={usage.articles.length ? 'xs' : 0} size="xs">
                                {L.documentUsageNodes}
                            </Text>
                            {usage.nodes.map((n) => (
                                <Anchor key={n.id} size="sm" onClick={() => onSelectNode(n.id)}>
                                    {n.name}
                                </Anchor>
                            ))}
                        </>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};

/** One row - table layout, `md` and up. */
const DocumentRow = ({
    group,
    locale,
    usageIndex,
    systemNodeId,
    documentLinkBase,
    onSelectNode,
}: {
    group: MediaGroup<DocumentIndexItem>;
    locale: string;
    usageIndex?: Map<string, DocumentUsage>;
    systemNodeId?: string;
    documentLinkBase?: string;
    onSelectNode?: (nodeId: string) => void;
}) => {
    const { doc, selectedLanguage, setSelectedLanguage, usage, open } = useResolvedDocument(
        group,
        locale,
        usageIndex,
        systemNodeId,
        documentLinkBase
    );

    return (
        <Table.Tr
            className="cursor-pointer"
            tabIndex={0}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            <Table.Td onClick={(e) => e.stopPropagation()}>
                <WishlistButton id={group.primary.documentId ?? group.primary.assetId} size={16} type="document" />
            </Table.Td>
            <Table.Td>
                <DocumentThumbnail alt={doc.descr ?? doc.assetId} />
            </Table.Td>
            <Table.Td>
                <Text>{doc.descr ?? doc.assetId}</Text>
            </Table.Td>
            <Table.Td onClick={(e) => e.stopPropagation()}>
                <DocumentLanguagePicker
                    current={selectedLanguage ?? doc.language ?? ''}
                    locale={locale}
                    variants={group.variants}
                    width={130}
                    onSelect={setSelectedLanguage}
                />
            </Table.Td>
            <Table.Td>
                <Text c="dimmed" size="sm">
                    {doc.editionDate ?? '–'}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text c="dimmed" size="sm">
                    {doc.docType ? humanizeDocType(doc.docType) : '–'}
                </Text>
            </Table.Td>
            {usageIndex && onSelectNode && (
                <Table.Td onClick={(e) => e.stopPropagation()}>
                    <UsageTrigger usage={usage} onSelectNode={onSelectNode} />
                </Table.Td>
            )}
        </Table.Tr>
    );
};

/** Same document, stacked card layout - replaces the table below `md` entirely (see
 * DocumentSearchResults - a table's fixed columns can't reasonably wrap to fit a mobile screen). */
const DocumentCard = ({
    group,
    locale,
    usageIndex,
    systemNodeId,
    documentLinkBase,
    onSelectNode,
}: {
    group: MediaGroup<DocumentIndexItem>;
    locale: string;
    usageIndex?: Map<string, DocumentUsage>;
    systemNodeId?: string;
    documentLinkBase?: string;
    onSelectNode?: (nodeId: string) => void;
}) => {
    const { doc, selectedLanguage, setSelectedLanguage, usage, open } = useResolvedDocument(
        group,
        locale,
        usageIndex,
        systemNodeId,
        documentLinkBase
    );
    // Only what the document actually has, no placeholders: a missing edition date used to print
    // as "· – · Product Data Sheet" (review screenshot), a dash and two separators for nothing.
    const meta = [doc.editionDate, doc.docType ? humanizeDocType(doc.docType) : undefined]
        .filter((part): part is string => Boolean(part))
        .join(' · ');

    return (
        <Card
            className="cursor-pointer"
            padding="sm"
            radius="md"
            role="button"
            tabIndex={0}
            w="100%"
            withBorder
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            {/* ONE child, a vertical Stack: as three direct children the rows ended up side by side
                on the phone (review screenshot - title | wishlist | language + meta | usage all in
                one line, the title wrapped to three lines). @uag/doka-design lays the card out as
                a flex ROW, so the stacking has to be explicit rather than left to the Card - and so
                does the WIDTH: as a flex item the Stack shrank to its content, which left the
                Merkliste toggle sitting right behind the title and "1 Artikel" at a different x on
                every card (review screenshot). `w="100%"` makes the rows span the card, so the toggle
                and the usage link line up on the right edge. */}
            <Stack gap={8} w="100%">
                <Group align="flex-start" gap="xs" justify="space-between" wrap="nowrap">
                    <Text fw={500} style={{ minWidth: 0, flex: 1 }}>
                        {doc.descr ?? doc.assetId}
                    </Text>
                    <div onClick={(event) => event.stopPropagation()}>
                        <WishlistButton
                            id={group.primary.documentId ?? group.primary.assetId}
                            size={18}
                            type="document"
                        />
                    </div>
                </Group>
                {meta && (
                    <Text c="dimmed" size="sm">
                        {meta}
                    </Text>
                )}
                <Group align="center" gap="sm" justify="space-between" wrap="wrap">
                    <div onClick={(event) => event.stopPropagation()}>
                        <DocumentLanguagePicker
                            current={selectedLanguage ?? doc.language ?? ''}
                            locale={locale}
                            variants={group.variants}
                            width={130}
                            onSelect={setSelectedLanguage}
                        />
                    </div>
                    {usageIndex && onSelectNode && (
                        <div onClick={(event) => event.stopPropagation()}>
                            <UsageTrigger usage={usage} onSelectNode={onSelectNode} />
                        </div>
                    )}
                </Group>
            </Stack>
        </Card>
    );
};

export const DocumentSearchResults = ({
    items,
    usageIndex,
    locale,
    sort,
    onSort,
    systemNodeId,
    documentLinkBase,
    onSelectNode
}: DocumentSearchResultsProps) => {
    const { L } = useCatalogLabels();

    return (
        <Stack gap="sm">
            {items.length === 0 ? (
                <Text c="dimmed">{L.documentIndexNoResults}</Text>
            ) : (
                <>
                    {/* Table from `md` up; a table's fixed-width columns can't reasonably wrap to
                    fit a mobile screen (see chat/code review - a plain Table.ScrollContainer just
                    forced sideways scrolling to read a row). The stacked cards below replace it
                    entirely on mobile instead. minWidth is a fallback for a narrow desktop
                    window, not the mobile fix. `type="native"` for the same reason as in ProductGrid:
                    Mantine's ScrollArea eats the wheel event over the table and blocks page
                    scrolling. */}
                    <Table.ScrollContainer className="hidden md:block" minWidth={810} type="native">
                        <Table verticalSpacing="xs" withRowBorders={false} highlightOnHover striped>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={40} />
                                    {/* Preview image, right behind the Merkliste column (see chat) -
                                        no caption, same as the Merkliste column next to it. */}
                                    <Table.Th w={THUMBNAIL_SIZE + 16} />
                                    <SortableTh label={L.documentTitle} sort={sort} sortKey="title" onSort={onSort} />
                                    <SortableTh
                                        label={L.documentLanguage}
                                        sort={sort}
                                        sortKey="language"
                                        width={130}
                                        onSort={onSort}
                                    />
                                    <SortableTh
                                        label={L.documentEditionDate}
                                        sort={sort}
                                        sortKey="edition"
                                        width={110}
                                        onSort={onSort}
                                    />
                                    <SortableTh
                                        label={L.documentCategory}
                                        sort={sort}
                                        sortKey="docType"
                                        width={160}
                                        onSort={onSort}
                                    />
                                    {/* Inside a system the counts are scoped to that system (see
                                        the prop's own comment), so the header says so. Dropped
                                        entirely where there is nothing to show (an article's own
                                        documents). */}
                                    {usageIndex && (
                                        <Table.Th w={160}>
                                            {systemNodeId ? L.documentUsageInSystem : L.documentUsage}
                                        </Table.Th>
                                    )}
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {items.map((group) => (
                                    <DocumentRow
                                        key={group.primary.documentId ?? group.primary.assetId}
                                        documentLinkBase={documentLinkBase}
                                        group={group}
                                        locale={locale}
                                        systemNodeId={systemNodeId}
                                        usageIndex={usageIndex}
                                        onSelectNode={onSelectNode}
                                    />
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    <Stack className="md:hidden" gap="xs">
                        {items.map((group) => (
                            <DocumentCard
                                key={group.primary.documentId ?? group.primary.assetId}
                                documentLinkBase={documentLinkBase}
                                group={group}
                                locale={locale}
                                systemNodeId={systemNodeId}
                                usageIndex={usageIndex}
                                onSelectNode={onSelectNode}
                            />
                        ))}
                    </Stack>
                </>
            )}
        </Stack>
    );
};
